import { AxiosInstance } from "axios"
import { ReportDetails } from "../../controlKitchenApi"
import { SurveySummary } from "../../admin/survey/surveyTypes"
import { Period } from "../../../dashboards/dashboardTypes"
import {
    AverageAmount,
    CheckinSummary,
    SalesSummary,
    ShopSalesSummary,
    SubscriptionsSale,
    ReportDetailsType,
    ReportDetailsPerHour,
    MeetingServiceSaleDetail,
    DeviceReportResponse,
    ReportSummaryPos,
    SalesSummaryPos,
    ReportSummaryNumberOfUsers,
    NumberOfUsers,
    ReportSummaryNumberOfTransactions,
    PosSaleNumberOfTransactions,
    MeetingServiceSale,
} from "./merchantHomeTypes"
import { DashboardNotifications, MeetingServiceOrder } from "../production/meetingService/meetingServiceTypes"

export enum MerchantHomeQuery {
    TODAYS_POS_SALES = "todays-POS-sales",
    POS_AVERAGE_AMOUNT = "POS-average-amount",
    TODAYS_SUBSCRIPTIONS_SALES = "todays-subscriptions-sales",
    TODAYS_CONTROL_CODE = "todays-control-code",
    TODAYS_OFFERS = "todays-offers",
    MEETING_SERVICE_ORDER_SUMMARY_AWAITING_APPROVAL = "meeting-service-order-summary-awaiting-approval",
    MEETING_SERVICE_ORDER_SUMMARY_STATUS = "meeting-service-order-summary-status",
    MEETING_SERVICE_ORDER_SUMMARY_TIME_PERIOD = "meeting-service-order-summary-time-period",
    MEETING_SERVICE_ORDERS_PER_HOURS = "meeting-service-orders-per-hours",
    MEETING_SERVICE_LIMIT = "meeting-service-limit",
    CHECK_IN_SUMMARY = "check-in-summary",
    SURVEY_RATING_SUMMARIES = "survey-rating-summaries",
    DEVICE_REPORT = "device-report-for-merchant",
    STATISTICS_CHECK_IN_SUMMARY = "statistics-check-in-summary",
    STATISTICS_POS_SALES = "statistics-POS-sales",
    STATISTICS_NUMBER_OF_TRANSACTIONS = "POS-number-of-transactions",
    STATISTICS_NUMBER_OF_USERS = "statistics-number-of-users",
}

type FetchTodaysControlCodeParams = Readonly<{
    merchantId: number
}>

export type FetchTodaysControlCodeResponse = Readonly<{
    receipt: {
        controlCode: string | number
    }
}>

export async function fetchTodaysControlCode(
    { merchantId }: FetchTodaysControlCodeParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchTodaysControlCodeResponse> {
    const url = `/merchants/${merchantId}/dashboard/receipt/controlCode`

    const response = await axios.get(url, {
        params: {},
        signal,
    })

    return response.data
}

type FetchTodaysOffersParams = Readonly<{
    merchantId: number
}>

export type FetchTodaysOffersResponse = Readonly<{
    marketing: {
        numberOfTodaysOffers: number
    }
}>

export async function fetchTodaysOffers(
    { merchantId }: FetchTodaysOffersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchTodaysOffersResponse> {
    const url = `/merchants/${merchantId}/dashboard/marketing/NumberOfOffers`

    const response = await axios.get(url, {
        params: {},
        signal,
    })

    return response.data
}

type FetchShopSalesParams = Readonly<{
    merchantId: number
}>

export type FetchShopSalesResponse = Readonly<{
    reportDetails: ReportDetails
    reportSummary: SalesSummary
    shopSales: ShopSalesSummary[]
}>

export async function fetchShopSales(
    { merchantId }: FetchShopSalesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchShopSalesResponse> {
    const url = `/merchants/${merchantId}/dashboard/....`

    const response = await axios.get(url, {
        params: {},
        signal,
    })

    return response.data
}

type FetchTodaysPosSalesParams = Readonly<{
    merchantId: number
}>

export type FetchTodaysPosSalesResponse = Readonly<{
    reportDetails: ReportDetails
    posSale: SalesSummary
}>

export async function fetchTodaysPosSales(
    { merchantId }: FetchTodaysPosSalesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchTodaysPosSalesResponse> {
    const url = `/merchants/${merchantId}/dashboard/posSale/today`

    const response = await axios.get(url, {
        params: {},
        signal,
    })

    return response.data
}

type FetchCheckInSummaryParams = Readonly<{
    merchantId: number
    period?: Period
    periodInDays?: number
}>

export type FetchCheckInSummaryResponse = Readonly<{
    reportDetails: ReportDetails
    checkInSummary: CheckinSummary
}>

export async function fetchCheckInSummary(
    { merchantId, period, periodInDays }: FetchCheckInSummaryParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchCheckInSummaryResponse> {
    const url = `/merchants/${merchantId}/dashboard/checkIns`

    const response = await axios.get(url, {
        params: {
            period,
            periodInDays,
        },
        signal,
    })

    return response.data
}

type FetchSurveySummaryParams = Readonly<{
    merchantId: number
    period?: Period
    periodInDays?: number
}>

type FetchSurveySummaryResponse = Readonly<{
    surveys: SurveySummary[]
}>

export async function fetchSurveyRatingSummaries(
    { merchantId, period, periodInDays }: FetchSurveySummaryParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchSurveySummaryResponse> {
    const url = `/merchants/${merchantId}/dashboard/survey/ratingsSummary`

    const response = await axios.get(url, {
        params: {
            period,
            periodInDays,
        },
        signal,
    })

    return response.data
}

type PosAverageAmountParams = Readonly<{
    merchantId: number
    period: Period
}>

export type PosAverageAmountResponse = Readonly<{
    reportDetails: ReportDetails
    posSales: AverageAmount[]
}>

export async function fetchPosAverageAmount(
    { merchantId, period }: PosAverageAmountParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<PosAverageAmountResponse> {
    const url = `/merchants/${merchantId}/dashboard/posAvgAmount`

    const response = await axios.get(url, {
        params: {
            period,
        },
        signal,
    })

    return response.data
}

type SubscriptionsSalesParams = Readonly<{
    merchantId: number
    period: Period
}>

export type SubscriptionsSalesResponse = Readonly<{
    reportDetails: ReportDetails
    subscriptionSale: SubscriptionsSale[]
}>

export async function fetchSubscriptionsSales(
    { merchantId, period }: SubscriptionsSalesParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<SubscriptionsSalesResponse> {
    const url = `/merchants/${merchantId}/dashboard/subscriptionSale`

    const response = await axios.get(url, {
        params: {
            period,
        },
        signal,
    })

    return response.data
}

// Meeting service Api
type MeetingServiceOrderSummaryParams = Readonly<{
    period?: string
    status?: string
    overdueDays?: number
}>

export type MeetingServiceOrderSummaryResponse = Readonly<{
    reportDetails: ReportDetailsType
    posSales?: MeetingServiceSale[]
    meetingServiceSale?: MeetingServiceSale[]
}>
export async function fetchMeetingServiceOrderSummary(
    { merchantId, params }: { merchantId: number; params: MeetingServiceOrderSummaryParams },
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceOrderSummaryResponse> {
    const url = `/merchants/${merchantId}/dashboard/meetingService/numberOfOrders`

    const { data } = await axios.get(url, {
        params: {
            ...params,
        },
        signal,
    })

    return data
}

type FetchDashboardNotificationsParams = Readonly<{
    merchantId: number
}>

export type NotificationsStatus = {
    type: DashboardNotifications
    numberOfOrders: number
}

export type FetchDashboardNotificationsResponse = Readonly<{
    notifications: NotificationsStatus[]
}>

export async function fetchDashboardNotifications(
    { merchantId }: FetchDashboardNotificationsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchDashboardNotificationsResponse> {
    const url = `/merchants/${merchantId}/dashboard/notifications`

    const { data } = await axios.get(url, {
        signal,
    })

    return data
}

type MeetingServiceLimitParams = Readonly<{
    merchantId: number
    limit: number
}>

export type MeetingServiceLimitResponse = Readonly<{
    orders: MeetingServiceOrder[]
}>
export async function fetchMeetingServiceLimit(
    param: MeetingServiceLimitParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceLimitResponse> {
    const { merchantId, limit } = param
    const url = `/merchants/${merchantId}/dashboard/meetingService/orders`

    const { data } = await axios.get(url, {
        params: {
            limit,
        },
        signal,
    })

    return data
}

type MeetingServiceOrdersPerHourParams = Readonly<{
    merchantId: number
    period: string
}>

export type MeetingServiceOrdersPerHourResponse = Readonly<{
    reportDetails: ReportDetailsPerHour
    meetingServiceSale: MeetingServiceSaleDetail
}>
export async function fetchMeetingServiceOrdersPerHour(
    param: MeetingServiceOrdersPerHourParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<MeetingServiceOrdersPerHourResponse> {
    const { merchantId, period } = param
    const url = `/merchants/${merchantId}/dashboard/meetingService/numberOfOrdersPerHour`

    const { data } = await axios.get(url, {
        params: {
            period,
        },
        signal,
    })

    return data
}

type DeviceReportParams = Readonly<{
    merchantId: number
}>

export async function fetchDeviceReport(
    { merchantId }: DeviceReportParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<DeviceReportResponse> {
    const url = `/merchants/${merchantId}/dashboard/deviceReport`

    const { data } = await axios.get(url, {
        params: {},
        signal,
    })

    return data
}

type FetchPosSalesWithPeriodInDaysParams = Readonly<{
    merchantId: number
    periodInDays: number
}>

export type FetchPosSalesWithPeriodInDaysResponse = Readonly<{
    reportDetails: ReportDetails
    reportSummary: ReportSummaryPos
    posSales: SalesSummaryPos[]
}>

export async function fetchPosSalesWithPeriodInDays(
    { merchantId, periodInDays }: FetchPosSalesWithPeriodInDaysParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchPosSalesWithPeriodInDaysResponse> {
    const url = `/merchants/${merchantId}/dashboard/posSale`

    const { data } = await axios.get(url, {
        params: { periodInDays },
        signal,
    })

    return data
}

type FetchPosNumberOfTransactionsParams = Readonly<{
    merchantId: number
    periodInDays: number
}>

export type FetchPosNumberOfTransactionsResponse = Readonly<{
    reportDetails: ReportDetails
    reportSummary: ReportSummaryNumberOfTransactions
    posSales: PosSaleNumberOfTransactions[]
}>

export async function fetchPosNumberOfTransactions(
    { merchantId, periodInDays }: FetchPosNumberOfTransactionsParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchPosNumberOfTransactionsResponse> {
    const url = `/merchants/${merchantId}/dashboard/pos/numberOfTransactions`

    const { data } = await axios.get(url, {
        params: { periodInDays },
        signal,
    })

    return data
}

type FetchNumberOfUsersParams = Readonly<{
    merchantId: number
    periodInDays: number
}>

export type FetchNumberOfUsersResponse = Readonly<{
    reportDetails: ReportDetails
    reportSummary: ReportSummaryNumberOfUsers
    numberOfUsers: NumberOfUsers[]
}>

export async function fetchNumberOfUsers(
    { merchantId, periodInDays }: FetchNumberOfUsersParams,
    axios: AxiosInstance,
    signal?: AbortSignal,
): Promise<FetchNumberOfUsersResponse> {
    const url = `/merchants/${merchantId}/dashboard/numberOfUsers`

    const { data } = await axios.get(url, {
        params: { periodInDays },
        signal,
    })

    return data
}
